<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import type { GenericField } from '@Types';
import type { FieldSchema } from '../../../types';
import { capitalizeStr, formatPhoneNumber } from '@Helpers';
import { emailValidation, phoneValidation } from '../../../helpers';
import { checkDuplicatePersonService } from '../../../services';
import { useMFieldContactType } from '.';

export interface ContactFieldType {
  contact?: string | null;
  email?: string | null;
  error?: string;
  id: number;
  phone?: string | null;
  primary: boolean;
  type: 'work' | 'home' | 'other' | 'mobile';
}

interface PersonResult {
  _id: { $oid: string };
  addresses: string;
  full_name: string;
  id: string;
}

defineOptions({ inheritAttrs: false });

const { contactData, personId } = useMFieldContactType();

const props = withDefaults(
  defineProps<{
    contactType?: 'email' | 'phone';
    error?: string;
    field?: FieldSchema;
    options?: any[];
    placeholder?: string;
    required?: boolean;
    exclude_id?: string;
  }>(),
  {
    contactType: 'email',
    placeholder: 'components.forms.placeholder.common',
  }
);

const model = defineModel<ContactFieldType[]>({
  default: [{ id: 1, contact: null, type: 'home', primary: false }],
});

const checkTimeouts = ref<Record<number, ReturnType<typeof setTimeout> | null>>({});
const contactIdIndex = ref(1);
const isCheckingDuplicates = ref<Record<number, boolean>>({});
const isSelectingFromModals = ref<Record<number, boolean>>({});
const lastCheckedValues = ref<Record<number, string>>({});
const pendingBlurChecks = ref<Record<number, 'phone' | 'email' | null>>({});
const radioModel = ref<number>(1);
const requestCounters = ref<Record<number, number>>({});
const results = ref<Record<number, PersonResult[]>>({});
const showCards = ref<Record<number, boolean>>({});

const contactType = computed(() => props?.field?.settings?.contactType || props.contactType);
const excludeId = computed(() => props?.exclude_id || props.field?.settings?.exclude_id);
const isEmailType = computed(() => contactType.value === 'email');
const options = computed(() => props?.field?.edit?.options || props?.options || []);
const shouldCheckDuplicates = computed(() => props?.field?.settings?.checkForDuplicates === true);

watch(radioModel, (newId, oldId) => toggleRadioModel(newId, oldId));
watch(
  () => model,
  () => onModelUpdate(),
  { deep: true }
);

onMounted(() => onInit());

onUnmounted(() => onUnmount());

// EVENTS
function onInit() {
  setIds();
  resetDuplicateCheckState();
}

function onUnmount() {
  Object.keys(checkTimeouts.value).forEach((id) => {
    const entryId = Number(id);
    clearChecksAndPendingRequests(entryId);
  });
}

function onModelUpdate() {
  onPrimaryUpdate();

  model.value.forEach(({ contact, id }) => {
    if (contact !== null) validateEmailPhoneField(contact, id);
  });
}

function onNewContact() {
  const newId = contactIdIndex.value + 1;
  model.value.push({
    id: newId,
    contact: null,
    type: 'home',
    primary: !model.value.length,
  });

  ++contactIdIndex.value;

  // Initialize duplicate check state for new entry
  if (shouldCheckDuplicates.value) {
    requestCounters.value[newId] = 0;
    checkTimeouts.value[newId] = null;
    pendingBlurChecks.value[newId] = null;
    isSelectingFromModals.value[newId] = false;
    results.value[newId] = [];
    isCheckingDuplicates.value[newId] = false;
    lastCheckedValues.value[newId] = '';
    showCards.value[newId] = false;
  }
}

function onRemoveContact(toBeDeletedId: number) {
  const isLastItem = model.value.length === 1;
  const emptyContact = model.value[0].contact === null;

  // NOTE: Last entry in the array of items
  const lastItemWithEmptyContent = isLastItem && emptyContact;
  if (lastItemWithEmptyContent) return;

  const lastItemWithContent = isLastItem && !emptyContact;
  if (lastItemWithContent) return resetState();

  // Clean up duplicate check state for removed entry
  if (shouldCheckDuplicates.value) {
    clearChecksAndPendingRequests(toBeDeletedId);
    delete requestCounters.value[toBeDeletedId];
    delete checkTimeouts.value[toBeDeletedId];
    delete pendingBlurChecks.value[toBeDeletedId];
    delete isSelectingFromModals.value[toBeDeletedId];
    delete results.value[toBeDeletedId];
    delete isCheckingDuplicates.value[toBeDeletedId];
    delete lastCheckedValues.value[toBeDeletedId];
    delete showCards.value[toBeDeletedId];
  }

  // NOTE: Validation for primary item
  let newEntries: ContactFieldType[] = model.value.filter(({ id }) => id !== toBeDeletedId);
  const newEntriesHasPrimary = newEntries.some(({ primary }) => primary);

  if (!newEntriesHasPrimary) newEntries[0].primary = true;

  model.value = newEntries;
}

function onTypeUpdate(index: number, value: any) {
  model.value.forEach((entry) => {
    const matchEntry = entry.id === index;

    if (matchEntry) entry.type = value.value.value;
  });
}

function onPrimaryUpdate() {
  const primaryContact = model.value.filter(({ primary }) => primary);
  const noActivePrimary = !primaryContact.length;
  if (noActivePrimary) return;

  radioModel.value = primaryContact[0].id;
}
// HELPERS
function setIds() {
  if (!model.value.length) return;

  model.value.forEach((entry, index) => (entry.id = index + 1));
  contactIdIndex.value = model.value.length;
}

function toggleRadioModel(newSelectionId: number, oldSelectionId: number) {
  const contactData = {
    newId: '',
    oldId: '',
  };

  model.value.forEach(({ contact, id }) => {
    if (id === newSelectionId) contactData.newId = contact ?? '';
    if (id === oldSelectionId) contactData.oldId = contact ?? '';
  });

  const emptyContactNewId = contactData.newId;
  if (!emptyContactNewId) {
    setError(newSelectionId, getEmptyError());

    const emptyContactOldId = contactData.oldId;
    if (emptyContactOldId) radioModel.value = oldSelectionId;

    return;
  }

  model.value.forEach((entry) => (entry.primary = radioModel.value === entry.id));
}

function resetState() {
  contactIdIndex.value = 1;
  radioModel.value = 1;

  model.value = [
    {
      id: 1,
      contact: null,
      type: 'home',
      primary: true,
      error: getEmptyError(),
    },
  ];

  // Reset duplicate check state
  if (shouldCheckDuplicates.value) {
    resetDuplicateCheckState();
  }
}

// DUPLICATE CHECKING
function resetDuplicateCheckState() {
  model.value.forEach((entry) => {
    const id = entry.id;
    requestCounters.value[id] = 0;
    checkTimeouts.value[id] = null;
    pendingBlurChecks.value[id] = null;
    isSelectingFromModals.value[id] = false;
    results.value[id] = [];
    isCheckingDuplicates.value[id] = false;
    lastCheckedValues.value[id] = '';
    showCards.value[id] = false;
  });
}

function clearChecksAndPendingRequests(entryId: number) {
  if (checkTimeouts.value[entryId]) {
    clearTimeout(checkTimeouts.value[entryId]!);
    checkTimeouts.value[entryId] = null;
  }

  requestCounters.value[entryId] = (requestCounters.value[entryId] || 0) + 1;
  pendingBlurChecks.value[entryId] = null;
}

function handleBlur(entryId: number) {
  if (!shouldCheckDuplicates.value) return;

  const field = isEmailType.value ? 'email' : 'phone';
  const ongoingCheckOrInteraction =
    isCheckingDuplicates.value[entryId] || isSelectingFromModals.value[entryId] || showCards.value[entryId];
  if (ongoingCheckOrInteraction) return;

  pendingBlurChecks.value[entryId] = field;

  // NOTE: This timeout is for handling browser event cycles.
  const blurCheckTimeout = 2;
  setTimeout(() => {
    if (pendingBlurChecks.value[entryId] !== field) return;

    pendingBlurChecks.value[entryId] = null;
    handleDuplicateCheck(entryId, field);
  }, blurCheckTimeout);
}

function handleDuplicateCheck(entryId: number, field: string) {
  if (!shouldCheckDuplicates.value) return;

  const ongoingCheckOrInteraction =
    isCheckingDuplicates.value[entryId] || isSelectingFromModals.value[entryId] || showCards.value[entryId];
  if (ongoingCheckOrInteraction) return;

  if (checkTimeouts.value[entryId]) {
    clearTimeout(checkTimeouts.value[entryId]!);
    checkTimeouts.value[entryId] = null;
  }

  checkTimeouts.value[entryId] = performDuplicateCheck(entryId, field) as unknown as ReturnType<typeof setTimeout>;
}

function performDuplicateCheck(entryId: number, field: string) {
  if (!shouldCheckDuplicates.value) return;

  const entry = model.value.find((e) => e.id === entryId);
  if (!entry) return;

  const currentValue = entry.contact?.trim();
  if (!currentValue) return;

  const normalizedValue: string =
    field === 'phone' ? formatPhoneNumber(currentValue) || currentValue : currentValue.toLowerCase();

  if (lastCheckedValues.value[entryId] === normalizedValue) return;

  requestCounters.value[entryId] = (requestCounters.value[entryId] || 0) + 1;
  const currentRequestId = requestCounters.value[entryId];

  results.value[entryId] = [];
  showCards.value[entryId] = false;

  const PAYLOAD = {
    entity: 'people',
    field,
    value: normalizedValue,
    exclude_id: excludeId.value || undefined,
  };

  isCheckingDuplicates.value[entryId] = true;
  lastCheckedValues.value[entryId] = normalizedValue;

  checkDuplicatePersonService(PAYLOAD)
    .then((result: any) => {
      if (currentRequestId !== requestCounters.value[entryId]) return;
      if (!result || !result.length) return;

      results.value[entryId] = result;
      showCards.value[entryId] = true;
    })
    .finally(() => {
      if (currentRequestId !== requestCounters.value[entryId]) return;
      isCheckingDuplicates.value[entryId] = false;
    });
}

function setPersonData(entryId: number, _data: PersonResult) {
  clearChecksAndPendingRequests(entryId);

  isSelectingFromModals.value[entryId] = true;
  showCards.value[entryId] = false;
  lastCheckedValues.value[entryId] = '';

  contactData.value = _data;
  personId.value = _data?._id?.$oid;

  const modalCloseTimeout = 50;
  setTimeout(() => {
    isSelectingFromModals.value[entryId] = false;
  }, modalCloseTimeout);
}

function handleCloseModal(entryId: number) {
  showCards.value[entryId] = false;
  lastCheckedValues.value[entryId] = '';
}

// VALIDATIONS
function validateEmailPhoneField(contact: string | undefined | null, id: number) {
  if (!contact) return setError(id, getEmptyError());

  const isInvalid = isEmailType.value ? !emailValidation(contact) : !phoneValidation(contact);
  if (isInvalid) return setError(id, getInvalidError());

  const contactsInTheModel = model.value
    .map(({ contact }) => contact)
    .filter((contactModel) => contactModel === contact);
  const isDuplicated = contactsInTheModel.length > 1;
  if (isDuplicated) return setError(id, getDuplicatedError());

  return setError(id, '');
}

function setError(id: number, error: string) {
  model.value.forEach((entry) => {
    const matchEntry = entry.id === id;

    if (matchEntry) entry.error = error;
  });
}

function getEmptyError() {
  const isRequired = props.field?.settings.required;
  if (!isRequired) return '';

  const EMPTY_EMAIL = 'components.forms.errors.addEmail';
  const EMPTY_PHONE = 'components.forms.errors.addPhone';

  return isEmailType.value ? EMPTY_EMAIL : EMPTY_PHONE;
}

function getInvalidError() {
  const INVALID_EMAIL = 'components.forms.errors.validEmail';
  const INVALID_PHONE = 'components.forms.errors.validPhone';

  return isEmailType.value ? INVALID_EMAIL : INVALID_PHONE;
}

function getDuplicatedError() {
  const DUPLICATED_EMAIL = 'components.forms.errors.duplicatedEmail';
  const DUPLICATED_PHONE = 'components.forms.errors.duplicatedPhone';

  return isEmailType.value ? DUPLICATED_EMAIL : DUPLICATED_PHONE;
}
</script>

<template>
  <div
    v-for="entry in model"
    class="mb-2"
    :key="entry.id"
  >
    <AFieldError :error="entry?.error" />

    <InputGroup>
      <InputGroupAddon :class="['h-9 w-36 p-0', { 'af-field-error': !!entry?.error }]">
        <AFieldDropdown
          v-model="entry.type"
          class="!h-9 !w-min max-w-36"
          customClass="!border-x-0 rounded-none ring-0 outline-none"
          inputTextSize="text-xs"
          optionLabel="name"
          dontShowClearTrigger
          :options
          :error="entry?.error"
          :placeholder="capitalizeStr(entry.type) || $t('common.type')"
          @change="(value: any) => onTypeUpdate(entry.id, value as GenericField)"
        />
      </InputGroupAddon>

      <AFieldEmail
        v-if="isEmailType"
        v-model="entry.contact"
        class="w-full rounded-none"
        :error="entry?.error"
        :required="true"
        :debounceTime="0"
        :inProgress="shouldCheckDuplicates ? isCheckingDuplicates[entry.id] : false"
        @change="(contact: string | undefined | null) => validateEmailPhoneField(contact, entry.id)"
        @blur="shouldCheckDuplicates ? handleBlur(entry.id) : undefined"
      />

      <AFieldPhone
        v-else
        v-model="entry.contact"
        class="w-full"
        inputClass="w-full rounded-none border-r-0"
        :error="entry?.error"
        :required="true"
        :debounceTime="0"
        :inProgress="shouldCheckDuplicates ? isCheckingDuplicates[entry.id] : false"
        @change="(contact: string) => validateEmailPhoneField(contact, entry.id)"
        @blur="shouldCheckDuplicates ? handleBlur(entry.id) : undefined"
      />

      <InputGroupAddon :class="['h-9 p-0', { 'af-field-error': !!entry?.error }]">
        <RadioButton
          v-model="radioModel"
          v-tooltip.top="$t('tooltip.primary')"
          name="radio"
          :inputId="`${entry.id}`"
          :invalid="!!entry?.error"
          :value="entry.id"
        />
      </InputGroupAddon>

      <InputGroupAddon :class="['h-9 p-0', { 'af-field-error': !!entry?.error }]">
        <Button
          outlined
          class="h-fit w-fit border-none px-2 py-1"
          icon="pi pi-trash text-sm"
          severity="danger"
          @click="onRemoveContact(entry.id)"
        />
      </InputGroupAddon>
    </InputGroup>
  </div>

  <Button
    outlined
    class="af-button w-fit"
    icon="pi pi-plus"
    :label="$t(`common.${contactType}`)"
    @click="onNewContact"
  />

  <Teleport to="body">
    <template
      v-for="entry in model"
      :key="`card-${entry.id}`"
    >
      <AFieldPersonCard
        v-if="shouldCheckDuplicates && showCards[entry.id]"
        :hasConfirmationStep="field?.settings?.hasConfirmationStep"
        :mode="field?.settings?.mode"
        :results="results[entry.id] || []"
        @onClose="() => handleCloseModal(entry.id)"
        @onUsePerson="(data: any) => setPersonData(entry.id, data)"
      />
    </template>
  </Teleport>
</template>
